import { useMemo } from 'react'
import {
  AlertTriangle,
  Boxes,
  GitBranch,
  RefreshCw,
  Table as TableIcon,
} from 'lucide-react'
import { useQueryClient } from '@tanstack/react-query'
import { Button } from '@/components/ui'
import {
  useActivityFeed,
  useCofaPipelineCount,
  useCofaStatsSnapshot,
  useFulfillmentRiskSummary,
  useOpenOrdersCount,
} from '../api/queries'
import { ActivityFeed } from '../components/ActivityFeed'
import { AttentionRequired } from '../components/AttentionRequired'
import { CriticalAlertStrip, type CriticalAlert } from '../components/CriticalAlertStrip'
import {
  ModuleSummaryCard,
  type ModuleSummaryMetric,
} from '../components/ModuleSummaryCard'

export function DashboardPage() {
  const qc = useQueryClient()

  const orders = useOpenOrdersCount()
  const pipeline = useCofaPipelineCount()
  const cofaStats = useCofaStatsSnapshot()
  const risk = useFulfillmentRiskSummary()
  const activity = useActivityFeed(20)

  const alerts = useMemo<CriticalAlert[]>(() => {
    const out: CriticalAlert[] = []
    if ((risk.data?.CRITICAL ?? 0) > 0) {
      out.push({
        label: `${risk.data!.CRITICAL} critical fulfillment risks`,
        path: '/fulfillment',
      })
    }
    if ((pipeline.data?.generation_failed ?? 0) > 0) {
      out.push({
        label: `${pipeline.data!.generation_failed} CofA generations failed`,
        path: '/cofa',
      })
    }
    return out
  }, [risk.data, pipeline.data])

  const todayLabel = useMemo(() => {
    const now = new Date()
    return now.toLocaleDateString(undefined, {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
    })
  }, [])

  const ordersMetrics: ModuleSummaryMetric[] = [
    { label: 'Open', value: (orders.data?.total ?? 0).toLocaleString() },
    {
      label: 'Overdue',
      value: (orders.data?.overdue ?? 0).toLocaleString(),
      tone: (orders.data?.overdue ?? 0) > 0 ? 'danger' : 'default',
    },
  ]

  const cofaMetrics: ModuleSummaryMetric[] = [
    {
      label: 'Awaiting',
      value: (pipeline.data?.awaiting_approval ?? 0).toLocaleString(),
      tone: (pipeline.data?.awaiting_approval ?? 0) > 0 ? 'warning' : 'default',
    },
    {
      label: 'Failed',
      value: (pipeline.data?.generation_failed ?? 0).toLocaleString(),
      tone: (pipeline.data?.generation_failed ?? 0) > 0 ? 'danger' : 'default',
    },
  ]

  const fulfillmentMetrics: ModuleSummaryMetric[] = [
    {
      label: 'Critical',
      value: (risk.data?.CRITICAL ?? 0).toLocaleString(),
      tone: (risk.data?.CRITICAL ?? 0) > 0 ? 'danger' : 'default',
    },
    {
      label: 'Action',
      value: (risk.data?.ACTION_REQUIRED ?? 0).toLocaleString(),
      tone: (risk.data?.ACTION_REQUIRED ?? 0) > 0 ? 'warning' : 'default',
    },
  ]

  const inventoryMetrics: ModuleSummaryMetric[] = [
    {
      label: 'Missing SPI',
      value: (cofaStats.data?.missing_spi ?? 0).toLocaleString(),
      tone: (cofaStats.data?.missing_spi ?? 0) > 0 ? 'warning' : 'default',
    },
    {
      label: 'Aging > 30d',
      value: (cofaStats.data?.aging_over_30d ?? 0).toLocaleString(),
      tone: (cofaStats.data?.aging_over_30d ?? 0) > 0 ? 'danger' : 'default',
    },
  ]

  const refreshAll = () => {
    void qc.invalidateQueries({ queryKey: ['dashboard'] })
  }

  return (
    <div className="flex h-full flex-col">
      <header className="flex items-end justify-between gap-4 border-b border-border-subtle bg-surface px-6 py-3">
        <div className="flex flex-col gap-0.5">
          <h1 className="text-role-page-title leading-tight tracking-tight">
            Today
          </h1>
          <p className="text-role-section-label">
            {todayLabel}
          </p>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={refreshAll}
          loading={orders.isFetching || pipeline.isFetching}
        >
          <RefreshCw size={14} /> Refresh
        </Button>
      </header>

      <CriticalAlertStrip alerts={alerts} />

      <div className="flex min-w-0 flex-1 flex-col gap-6 overflow-auto bg-canvas p-6">
        <section>
          <h2 className="mb-2 text-role-section-label">
            Modules
          </h2>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <ModuleSummaryCard
              icon={TableIcon}
              title="Orders"
              path="/orders"
              metrics={ordersMetrics}
              loading={orders.isLoading}
              error={orders.isError}
            />
            <ModuleSummaryCard
              icon={GitBranch}
              title="CofA Pipeline"
              path="/cofa"
              metrics={cofaMetrics}
              loading={pipeline.isLoading}
              error={pipeline.isError}
            />
            <ModuleSummaryCard
              icon={AlertTriangle}
              title="Fulfillment"
              path="/fulfillment"
              metrics={fulfillmentMetrics}
              loading={risk.isLoading}
              error={risk.isError}
            />
            <ModuleSummaryCard
              icon={Boxes}
              title="Inventory health"
              path="/inventory"
              metrics={inventoryMetrics}
              loading={cofaStats.isLoading}
              error={cofaStats.isError}
            />
          </div>
        </section>

        <section className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <div className="flex flex-col gap-2">
            <h2 className="text-role-section-label">
              Activity feed
            </h2>
            <ActivityFeed entries={activity.data ?? []} />
          </div>
          <AttentionRequired />
        </section>
      </div>
    </div>
  )
}
